// services/authSyncService.js - v3.0 Multi-Tab Authentication Synchronization

class AuthSyncService {
  constructor() {
    this.channelName = 'host_me_auth_channel';
    this.channel = null;
    this.listeners = new Set();

    // Initialize BroadcastChannel if supported
    if ('BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(this.channelName);
      this.setupMessageHandler();
    } else {
      console.warn('⚠️  BroadcastChannel not supported - multi-tab sync disabled');
    }
  }

  /**
   * Setup message handler for incoming sync messages
   * @private
   */
  setupMessageHandler() {
    if (!this.channel) return;

    this.channel.onmessage = (event) => {
      const { type, payload } = event.data;

      // Notify all registered listeners
      this.listeners.forEach(listener => {
        try {
          listener(type, payload);
        } catch (error) {
          console.error('❌ Listener error:', error);
        }
      });
    };
  }

  /**
   * Broadcast authentication event to other tabs
   * @param {String} type - Event type (login, logout, token_refresh, etc.)
   * @param {Object} payload - Event data
   */
  broadcast(type, payload = {}) {
    if (!this.channel) return;

    const message = {
      type,
      payload,
      timestamp: Date.now()
    };

    this.channel.postMessage(message);
  }

  /**
   * Broadcast login event
   * @param {Object} user - User data
   * @param {String} accessToken - Access token
   */
  broadcastLogin(user, accessToken) {
    this.broadcast('LOGIN', {
      user,
      accessToken,
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast logout event
   */
  broadcastLogout() {
    this.broadcast('LOGOUT', {
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast token refresh event
   * @param {String} accessToken - New access token
   * @param {Number} expiresIn - Expiry in seconds
   */
  broadcastTokenRefresh(accessToken, expiresIn) {
    this.broadcast('TOKEN_REFRESH', {
      accessToken,
      expiresIn,
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast session expired event
   */
  broadcastSessionExpired() {
    this.broadcast('SESSION_EXPIRED', {
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast account status change
   * @param {String} status - New account status
   * @param {String} reason - Reason for status change
   */
  broadcastStatusChange(status, reason = '') {
    this.broadcast('STATUS_CHANGE', {
      status,
      reason,
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast impersonation start
   * @param {Object} targetUser - User being impersonated
   * @param {String} impersonationToken - Impersonation token
   */
  broadcastImpersonationStart(targetUser, impersonationToken) {
    this.broadcast('IMPERSONATION_START', {
      targetUser,
      impersonationToken,
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast impersonation end
   */
  broadcastImpersonationEnd() {
    this.broadcast('IMPERSONATION_END', {
      timestamp: Date.now()
    });
  }

  /**
   * Register a listener for sync events
   * @param {Function} callback - Callback(type, payload)
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Close the channel (cleanup)
   */
  close() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
      this.listeners.clear();
    }
  }

  /**
   * Check if multi-tab sync is supported
   * @returns {Boolean}
   */
  isSupported() {
    return 'BroadcastChannel' in window;
  }
}

// Create singleton instance
const authSyncService = new AuthSyncService();

export default authSyncService;
